import { Avatar, SearchInput, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useProfile from "@/hooks/useProfile";
import { getTeamQueryOptions } from "@/queries";
import { SearchMemberTeamSearchSchema } from "@/schemas";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useMemo } from "react";

export const Route = createFileRoute("/dashboard/teams/$id/members/search")({
	validateSearch: zodSearchValidator(SearchMemberTeamSearchSchema),
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const search = Route.useSearch({
		select: (state) => state.q ?? "",
	});
	const teamId = Route.useParams({
		select: (state) => state.id,
	});
	const seachType = Route.useSearch({
		select: (state) => state.type,
	});

	const { profile } = useProfile();

	const {
		data: { team },
	} = useSuspenseQuery(getTeamQueryOptions(teamId));

	const members = useMemo(() => {
		// @ts-expect-error
		const arr = team[seachType] as (typeof team)["users"];
		if (!search) return arr;
		return arr.filter(
			(member) =>
				member.fullname.toLowerCase().includes(search.toLowerCase()) ||
				member.first_name.toLowerCase().includes(search.toLowerCase()) ||
				member.last_name.toLowerCase().includes(search.toLowerCase()),
		);
	}, [team, seachType, search]);

	return (
		<>
			<BackAppBar
				rightContent={
					<SearchInput
						name="search"
						label="Search"
						placeholder="Search"
						className="h-10 px-2"
						value={search}
						onChange={(e) =>
							navigate({
								replace: true,
								search: {
									q: e.target.value || undefined,
								},
							})
						}
					/>
				}
			/>
			<div className="relative container">
				<List className="divide-y divide-gray-light">
					{members.length ? (
						<>
							{members.map((member) => (
								<ListItem
									key={member.id}
									disablePadding
									secondaryAction={
										member.id === team.creator?.id ? (
											<div className="bg-primary-light px-2 py-1 rounded-md">
												<Typography
													fontWeight={500}
													fontSize={12}
													className="text-primary"
												>
													Owner
												</Typography>
											</div>
										) : null
									}
								>
									<ListItemButton>
										<ListItemAvatar>
											<Avatar
												alt={member.first_name || "L"}
												className="!bg-gray-light !text-dark"
												src={member.avatar ?? "/broken-image.jpg"}
											/>
										</ListItemAvatar>
										<ListItemText
											primary={[
												member.fullname,
												profile?.id === member.id ? " (Me)" : "",
											].join("")}
										/>
									</ListItemButton>
								</ListItem>
							))}
						</>
					) : (
						<ListItem disablePadding>
							<ListItemText
								primary={
									<Typography
										align="center"
										fontWeight={700}
										className="!text-zinc-500"
									>
										{search
											? `No results found for ‘${search}’`
											: "No contacts"}
									</Typography>
								}
							/>
						</ListItem>
					)}
				</List>
			</div>
		</>
	);
}
